import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ImageIcon, Loader2, Newspaper, Pencil, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  deleteNoticia,
  listNoticias,
  type EstadoNoticia,
  type Noticia,
} from '@/features/noticias/api'
import { formatFechaPublicacion } from '@/features/noticias/format'

type FeaturedFilter = 'all' | 'yes' | 'no'
type EstadoFilter = 'all' | EstadoNoticia

export function NoticiasListPage(): ReactNode {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [featuredFilter, setFeaturedFilter] = useState<FeaturedFilter>('all')
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>('all')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [toDelete, setToDelete] = useState<Noticia | null>(null)

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['noticias', 'list', { estado: estadoFilter }],
    queryFn: ({ pageParam }) =>
      listNoticias({
        limit: 50,
        cursor: pageParam ?? null,
        ...(estadoFilter !== 'all' ? { estado: estadoFilter } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  })

  const items = useMemo<Noticia[]>(() => {
    return data?.pages.flatMap((p) => p.items) ?? []
  }, [data])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((n) => {
      if (q && !n.titulo.toLowerCase().includes(q)) return false
      if (featuredFilter === 'yes' && !n.featured) return false
      if (featuredFilter === 'no' && n.featured) return false
      if (desde && n.fechaPublicacion < desde) return false
      if (hasta && n.fechaPublicacion > hasta) return false
      return true
    })
  }, [items, search, featuredFilter, desde, hasta])

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNoticia(id),
    onSuccess: () => {
      toast.success('Noticia eliminada')
      void queryClient.invalidateQueries({ queryKey: ['noticias'] })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'No pudimos eliminar la noticia.'
      toast.error(msg)
    },
    onSettled: () => setToDelete(null),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Noticias</h1>
          <p className="text-sm text-muted-foreground">
            Gestiona el feed de noticias publicas y la noticia destacada.
          </p>
        </div>
        <Button onClick={() => navigate('/dashboard/noticias/nueva')}>
          <Plus className="h-4 w-4" />
          Nueva noticia
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="search">Buscar por título</Label>
          <Input
            id="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="estado">Estado</Label>
          <Select
            id="estado"
            value={estadoFilter}
            onChange={(e) => setEstadoFilter(e.target.value as EstadoFilter)}
          >
            <option value="all">Todos</option>
            <option value="PUBLICADA">Publicadas</option>
            <option value="BORRADOR">Borradores</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="featured">Destacada</Label>
          <Select
            id="featured"
            value={featuredFilter}
            onChange={(e) => setFeaturedFilter(e.target.value as FeaturedFilter)}
          >
            <option value="all">Todas</option>
            <option value="yes">Sí</option>
            <option value="no">No</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="desde">Desde</Label>
          <Input
            id="desde"
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hasta">Hasta</Label>
          <Input
            id="hasta"
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">Imagen</TableHead>
              <TableHead>Título</TableHead>
              <TableHead className="w-[120px]">Estado</TableHead>
              <TableHead className="w-[140px]">Fecha</TableHead>
              <TableHead className="w-[120px]">Destacada</TableHead>
              <TableHead className="w-[140px] text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((n) => (
              <TableRow key={n.id}>
                <TableCell>
                  <Thumbnail src={n.imagenUrl} alt={n.titulo} />
                </TableCell>
                <TableCell className="font-medium">{n.titulo}</TableCell>
                <TableCell>
                  {n.estado === 'PUBLICADA' ? (
                    <Badge variant="success">Publicada</Badge>
                  ) : (
                    <Badge variant="muted">Borrador</Badge>
                  )}
                </TableCell>
                <TableCell>{formatFechaPublicacion(n.fechaPublicacion)}</TableCell>
                <TableCell>
                  {n.featured ? (
                    <Badge variant="success">Destacada</Badge>
                  ) : (
                    <Badge variant="muted">No</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" asChild>
                      <Link
                        to={`/dashboard/noticias/${n.id}`}
                        aria-label={`Editar ${n.titulo}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Eliminar ${n.titulo}`}
                      onClick={() => setToDelete(n)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="border-t p-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'No pudimos cargar las noticias.'}
            </p>
          ) : filtered.length === 0 && items.length === 0 ? (
            <EmptyState
              icon={<Newspaper className="h-8 w-8" />}
              title="Aún no hay noticias"
              description="Crea la primera noticia para que aparezca en el feed de la app."
              action={
                <Button onClick={() => navigate('/dashboard/noticias/nueva')}>
                  <Plus className="h-4 w-4" />
                  Nueva noticia
                </Button>
              }
            />
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay noticias que coincidan con los filtros.
            </p>
          ) : hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Cargar mas
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar noticia</DialogTitle>
            <DialogDescription>
              {toDelete
                ? `"${toDelete.titulo}" se va a eliminar. Esta acción no se puede deshacer.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setToDelete(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (toDelete) deleteMutation.mutate(toDelete.id)
              }}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Thumbnail({ src, alt }: { src: string | null; alt: string }): ReactNode {
  if (!src) {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <ImageIcon className="h-5 w-5" />
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      className="h-12 w-12 rounded-md object-cover"
      loading="lazy"
    />
  )
}
